import { ComponentProps } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { PersonHelpers } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { GetWeavePopNotificationByType } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Chip, PhoneOutgoingIcon, Text } from '@frontend/design-system';
import { BaseNotificationComponentProps, BaseNotificationInner, BaseNotificationOuter } from './base';

type Props = BaseNotificationComponentProps<GetWeavePopNotificationByType<'inbox-robocall-tag'>>;

export const InboxRobocallTagNotificationComponent = ({ isHistorical, notification, emit, ...rest }: Props) => {
  const { t } = useTranslation('notifications');

  const { tagColor, tagName, personFirstName, personLastName, personPhone, startedAt } = notification.payload;

  const fullName = PersonHelpers.getFullName({
    FirstName: personFirstName,
    LastName: personLastName,
  });
  const displayName = fullName || (personPhone ? formatPhoneNumber(personPhone) : '');
  const formattedDate = startedAt ? dayjs(startedAt).format('MMM DD YYYY, hh:mm A') : '';

  const actions: ComponentProps<typeof BaseNotificationInner>['actions'] = [
    {
      label: t('View'),
      action: () => {
        emit({ action: 'view', payload: undefined }, notification);
      },
    },
  ];

  return (
    <BaseNotificationOuter
      actions={actions}
      notification={notification}
      emit={emit}
      isHistorical={isHistorical}
      {...rest}
    >
      <BaseNotificationInner
        onClose={() => emit({ action: 'ignore', payload: undefined }, notification)}
        actions={actions}
        title={t('Tag Applied to Robocall')}
        body={
          <section
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: ${theme.spacing(1)};
            `}
          >
            {tagName && <Chip.Tag color={tagColor}>{tagName}</Chip.Tag>}
            {displayName && <Text weight='bold'>{displayName}</Text>}
            {formattedDate && (
              <div
                css={css`
                  display: flex;
                  gap: ${theme.spacing(0.5)};
                  align-items: center;
                `}
              >
                <PhoneOutgoingIcon size={16} />
                <Text
                  size='medium'
                  css={css`
                    margin: 0;
                  `}
                >
                  {formattedDate}
                </Text>
              </div>
            )}
          </section>
        }
        location={notification.location}
        timestamp={notification.timestamp}
        id={notification.id}
        notificationType={notification.type}
        {...rest}
      />
    </BaseNotificationOuter>
  );
};
